// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@bg/App/Components/Notify";
import loadSystemList from "@bg/App/Handlers/CommonHandlers/LoadSystemList";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { GroupItem, GroupList } from "@bg/Overrides/Components/AddToListModal";
import { myListsDetailsOptionsStyles } from "@bg/Overrides/Widgets/MyListsDetails/MyListDetailsOptionsStyles";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { updateGroupNameHandler } from "@bg/XDL/Utilities/groupName";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import updateWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/UpdateWishList";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import setEditingSortOrder from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetEditingSortOrder";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    productInfo?: ProductInfo;
    creatingNewGroup: boolean;
    groupingModalIsOpen: boolean;
    closeModalCallback: () => void;
    callAfterSuccess?: (newGroupName: string, isMoving?: boolean) => void;
    isMovingItem?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const groupListName = state.context.website.properties["groupsListName"];
    return {
        wishListSettings: getSettingsCollection(state).wishListSettings,
        wishListDataView: getWishListState(state, state.pages.myListDetails.wishListId),
        editingSortOrder: state.pages.myListDetails.editingSortOrder,
        defaultListGroups: (state.xSystemList as any)[groupListName]?.values,
        uncategorizedGroupName: (getSettingsCollection(state).customSettings as any).unCategorizedGroupName as string,
        groupsNamingRegex: (getSettingsCollection(state).customSettings as any).groupsNamingRegex as string,
        groupListName,
    };
};

const mapDispatchToProps = {
    addToWishList: makeHandlerChainAwaitable<AddToWishListParameter, WishListModel>(addToWishList),
    setEditingSortOrder,
    loadSystemList,
    updateWishList,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export const styles = myListsDetailsOptionsStyles;

type State = {
    selectedGroup: string;
    currentStep: string;
    groupName: string;
    newGroupError: string;
    inProgress: boolean;
    groups: string[];
};

class CreateNewGroupModal extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            selectedGroup: "",
            currentStep: "",
            groupName: "",
            newGroupError: "",
            inProgress: false,
            groups: [],
        };
    }

    componentDidMount() {
        this.setState({ currentStep: "", groupName: "", newGroupError: "", selectedGroup: "", inProgress: false });
    }

    componentDidUpdate(_prevProps: Props) {
        if (
            !_prevProps.groupingModalIsOpen &&
            this.props.groupingModalIsOpen &&
            (!this.props.defaultListGroups || !this.props.defaultListGroups.length)
        ) {
            this.props.loadSystemList({
                systemListName: this.props.groupListName,
            });
        }

        if (
            this.props.wishListDataView &&
            this.props.wishListDataView.value &&
            this.props.wishListDataView.value.properties?.groups &&
            !this.state.groups.length
        ) {
            this.setDefaultListGroups();
        }
    }

    setDefaultListGroups() {
        const groups = JSON.parse(this.props.wishListDataView!.value!.properties?.groups || "");
        this.setState({ groups });
    }

    newGroupInputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ groupName: event.target.value });
        if (event.target.value) {
            this.setState({ newGroupError: "" });
        }
    };

    addNewGroupAndGoBack = () => {
        const { groupName } = this.state;

        const listGroups: string[] = JSON.parse(this.props.wishListDataView!.value!.properties?.groups || "[]");

        const { newGroupName, error } = updateGroupNameHandler(groupName, this.props.groupsNamingRegex, listGroups, {
            required: "Group Name is Required",
            exists: "Selected group already exists in the list",
            notValid: siteMessage("Custom_Group_Name_Error_Text") as string,
        });

        if (error) {
            this.setState({ newGroupError: error });
            return;
        }

        this.setState({ newGroupError: "", selectedGroup: newGroupName }, () => {
            this.updateWishListGroups();
        });
    };

    handleReturnFromNewGroup = () => {
        this.setState({ groupName: "", currentStep: "" });
    };

    handleGroupSelection = (groupName: string) => {
        if (this.state.selectedGroup.toLowerCase() === groupName.toLowerCase()) {
            this.setState({ selectedGroup: "" });
        } else {
            this.setState({ selectedGroup: groupName });
        }
    };

    addItemToListWithGroup = () => {
        const { selectedGroup } = this.state;
        const { toaster, isMovingItem } = this.props;
        const groups = JSON.parse(this.props.wishListDataView!.value!.properties?.groups || "");
        // check if group exists on the list if not update the list with the new group then do the addToWishList
        if (groups && groups.indexOf(selectedGroup.toLowerCase()) === -1 && selectedGroup.length > 0) {
            this.updateWishListGroups();
        } else {
            this.setState({ inProgress: true });
            this.addItemToList().then(() => {
                toaster.addToast({ body: translate("Item Added"), messageType: "success" });
                this.setState({ inProgress: false });
                this.props.callAfterSuccess?.(selectedGroup, isMovingItem);
                this.props.closeModalCallback?.();
            });
        }
    };

    updateWishListGroups = () => {
        const { selectedGroup } = this.state;
        const { wishListDataView, toaster, updateWishList } = this.props;
        const wishList = wishListDataView.value!;

        const groups = JSON.parse(this.props.wishListDataView!.value!.properties?.groups || "");

        const newGroups = groups;
        if (groups.length >= 8) {
            toaster.addToast({ body: siteMessage("Custom_MaxAllowedGroups"), messageType: "danger" });
            return;
        }
        if (groups.filter((name: string) => name.toLowerCase() === selectedGroup.toLowerCase()).length === 0) {
            this.setState({ inProgress: true });
            newGroups.push(selectedGroup);

            updateWishList({
                apiParameter: {
                    wishList: {
                        ...wishList,
                        properties: {
                            ...wishList.properties,
                            groups: newGroups.join(),
                        },
                    },
                },
                onSuccess: () => {
                    this.setState({ inProgress: false });
                    this.onSubmitSuccess();
                },
                onError: (error: any) => {
                    this.onSubmitError(error);
                    this.setState({ inProgress: false });
                },
            } as any);
        } else {
            toaster.addToast({
                body: translate("Selected group already exists in the list"),
                messageType: "warning",
            });
        }
    };

    resetModal = () => {
        this.setState(
            { currentStep: "", groupName: "", newGroupError: "", selectedGroup: "", inProgress: false },
            () => {
                this.props.closeModalCallback?.();
            },
        );
    };

    onSubmitSuccess = () => {
        const { isMovingItem, creatingNewGroup, toaster, callAfterSuccess, closeModalCallback } = this.props;
        if (creatingNewGroup) {
            callAfterSuccess?.(this.state.selectedGroup, isMovingItem);
            closeModalCallback?.();
        }

        toaster.addToast({
            body: translate("List Updated"),
            messageType: "success",
        });

        if (!creatingNewGroup) {
            this.props.closeModalCallback?.();
            this.addItemToList().then(() => {
                toaster.addToast({ body: translate("Item Added"), messageType: "success" });
                this.setState({ inProgress: false });
            });
        }
    };

    onSubmitError = (errorMessage: string) => {
        this.props.toaster.addToast({
            body: translate(`Error: ${errorMessage}`),
            messageType: "danger",
        });
        this.props.closeModalCallback?.();
    };

    addItemToList = async () => {
        const { productInfo, uncategorizedGroupName, wishListDataView, addToWishList } = this.props;
        const { selectedGroup } = this.state;
        const wishList = wishListDataView.value!;

        if (productInfo?.productId) {
            const newProductInfo = {
                ...productInfo,
                properties: {
                    isUpdateGroup: true,
                    grouping: JSON.stringify({
                        wishListLineGroups: [
                            {
                                groupName:
                                    selectedGroup.length > 0 ? selectedGroup : uncategorizedGroupName.toLowerCase(),
                                originalGroupName: "",
                                notes: "",
                                qtyOrdered: productInfo.qtyOrdered,
                                sortOrder: 0,
                            },
                        ],
                    }),
                },
            };
            await addToWishList({
                productInfos: [newProductInfo],
                selectedWishList: wishList,
                onError: error => {
                    this.props.toaster.addToast({ body: translate(error), messageType: "danger" });
                },
            });
        }
    };

    render() {
        const { currentStep, selectedGroup, inProgress, groupName, newGroupError } = this.state;
        const {
            groupingModalIsOpen,
            defaultListGroups,
            creatingNewGroup,
            isMovingItem,
            wishListDataView,
            uncategorizedGroupName,
            groupsNamingRegex,
        } = this.props;
        let customListGroupNamesToDispaly;

        if (wishListDataView.value?.properties?.groups !== undefined && defaultListGroups !== undefined) {
            const customListGroup = JSON.parse(wishListDataView.value?.properties?.groups);
            const defaultListGroupsNames = defaultListGroups.map((obj: any) => obj.name.toLowerCase());
            const customListGroupNames = customListGroup.filter(
                (name: any) => !defaultListGroupsNames.includes(name.toLowerCase()),
            );
            customListGroupNamesToDispaly = customListGroupNames.filter(
                (name: string) => name.toLowerCase() !== uncategorizedGroupName.toLowerCase(),
            );
        }

        const title =
            currentStep === "" ? siteMessage("Custom_List_ChooseGroup") : siteMessage("Custom_AddToList_AddGroup");
        return (
            <Modal
                className="add-top-padding"
                size={812}
                headline={title}
                isOpen={groupingModalIsOpen}
                handleClose={this.resetModal}
            >
                <StyledWrapper {...styles.stepWrapper} className={currentStep === "" ? "in-view" : ""}>
                    <StyledWrapper {...styles.groupsWrapper}>
                        {defaultListGroups &&
                            defaultListGroups.length > 0 &&
                            defaultListGroups.map((group: GroupList) => (
                                <Clickable
                                    className={
                                        selectedGroup.toLowerCase() === group.name.toLowerCase() ? "selected" : ""
                                    }
                                    key={group.id}
                                    {...styles.groupItem}
                                    onClick={() => this.handleGroupSelection(group.name)}
                                >
                                    <GroupItem imagePath={group.additionalInfo} groupName={group.description} />
                                </Clickable>
                            ))}
                        {!isMovingItem &&
                            !creatingNewGroup &&
                            customListGroupNamesToDispaly &&
                            customListGroupNamesToDispaly.length > 0 &&
                            customListGroupNamesToDispaly.map((group: any) => (
                                <Clickable
                                    className={selectedGroup.toLowerCase() === group.toLowerCase() ? "selected" : ""}
                                    key={group}
                                    {...styles.groupItem}
                                    onClick={() => this.handleGroupSelection(group)}
                                >
                                    <GroupItem imagePath="null" groupName={group} />
                                </Clickable>
                            ))}
                        <StyledWrapper {...styles.groupItem}>
                            <StyledWrapper {...styles.addNewGroupItem}>
                                <Button
                                    onClick={() => this.setState({ currentStep: "newGroup" })}
                                    {...styles.addNewButton}
                                >
                                    <ButtonIcon src="Plus" size={16} />
                                </Button>
                            </StyledWrapper>
                            <Typography
                                lineHeight={fontConfig.lineHeight.singleLineSmall}
                                transform="uppercase"
                                color="secondary"
                            >
                                {translate("New Blank Group")}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.buttonsWrapper} className="modal-actions">
                        <Button
                            {...styles.addButton}
                            onClick={creatingNewGroup ? this.updateWishListGroups : this.addItemToListWithGroup}
                            data-test-selector="productAddToListModal_addButton"
                            disabled={inProgress || (creatingNewGroup && !selectedGroup)}
                        >
                            {inProgress ? (
                                <LoadingSpinner color="secondary" size={28} />
                            ) : creatingNewGroup ? (
                                translate("Create Group")
                            ) : (
                                translate("Add Item To List")
                            )}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.stepWrapper} className={currentStep === "newGroup" ? "in-view" : ""}>
                    <TextField
                        label={translate("Group Name")}
                        value={groupName}
                        error={newGroupError}
                        maxLength={50}
                        onChange={this.newGroupInputChangeHandler}
                        placeholder={translate("New Group Name")}
                        {...styles.newListInput}
                        data-test-selector="productAddToListModal_newGroupName"
                    />
                    {groupsNamingRegex && (
                        <StyledWrapper {...styles.notifyStyle}>
                            <Notify messageType="info" body={siteMessage("Custom_GroupName_Limitations_Text")} />
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.buttonsWrapper} className="modal-actions">
                        <Button
                            {...styles.addButton}
                            onClick={this.addNewGroupAndGoBack}
                            data-test-selector="productAddToListModal_addButton"
                            disabled={inProgress || newGroupError.length > 0}
                        >
                            {translate("Save and Continue")}
                        </Button>
                        <Button {...styles.cancelButton} onClick={this.handleReturnFromNewGroup}>
                            {translate("Return to Previous Screen")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(CreateNewGroupModal));
